import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Paper,
} from '@material-ui/core';
import { FFTableFiltersProps, FFTableFilterValue } from './types';
import { useFFTableStyles } from './styles';
import { MDInput } from 'components/MDInput';
import { MDButton } from 'components/MDButton';

export function FFTableFilters<T>({
  columns,
  filters,
  onFilterChange,
}: FFTableFiltersProps<T>) {
  const classes = useFFTableStyles();
  const [filterValue, setFilterValue] = useState('');
  const [selectedColumn, setSelectedColumn] = useState<string | number>('');

  // Get filterable columns
  const filterableColumns = columns.filter(
    (column) => column.filterable !== false && column.type !== 'action'
  );

  // Initialize filter state from props
  useEffect(() => {
    if (filters.length > 0) {
      const filter = filters[0]; // We only support one filter at a time
      setSelectedColumn(filter.columnId);
      setFilterValue(filter.value);
    } else {
      // If no filters, set default column
      if (filterableColumns.length > 0 && !selectedColumn) {
        setSelectedColumn(filterableColumns[0].id);
      }
      setFilterValue('');
    }
  }, []);

  // Handle filter value change
  const handleFilterValueChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFilterValue(event.target.value);
  };

  // Handle column selection change
  const handleColumnChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setSelectedColumn(event.target.value as string);
    },
    [setSelectedColumn]
  );

  // Apply filter
  const handleApplyFilter = () => {
    if (!filterValue || !selectedColumn) {
      // Clear filters if filter value is empty
      onFilterChange([]);
      return;
    }

    const newFilter: FFTableFilterValue = {
      columnId: selectedColumn,
      value: filterValue,
      operator: 'contains',
    };

    onFilterChange([newFilter]);
  };

  // Clear filter
  const handleClearFilter = () => {
    setFilterValue('');
    onFilterChange([]);
  };

  // Handle Enter key press
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleApplyFilter();
    }
  };

  return (
    <Box p={2} display="flex" alignItems="center">
      <FormControl className={classes.filterItem} style={{ marginRight: 10 }}>
        <InputLabel id="column-select-label" style={{ top: -20 }}>
          Filter Column
        </InputLabel>
        <Select
          labelId="column-select-label"
          variant="outlined"
          value={selectedColumn}
          onChange={handleColumnChange}
          style={{ height: '40px', width: '200px' }}
          displayEmpty
        >
          {filterableColumns.map((column) => (
            <MenuItem key={`filter-column-${column.id}`} value={column.id}>
              {column.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        className={classes.filterItem}
        label="Filter Value"
        value={filterValue}
        onChange={handleFilterValueChange}
        onKeyUp={handleKeyPress}
        placeholder="Enter filter value"
        variant="outlined"
        size="small"
      />

      <div style={{ marginRight: '10px', marginLeft: '10px' }}>
        <MDButton
          variant="gradient"
          color="info"
          onClick={handleApplyFilter}
          className={classes.filterButton}
        >
          Apply
        </MDButton>
      </div>

      <div>
        <Button
          onClick={handleClearFilter}
          disabled={!filterValue}
          className={classes.filterButton}
        >
          Clear
        </Button>
      </div>
    </Box>
  );
}

export default FFTableFilters;
